/**
 * The per-quote controls and figures, shared by both My Pipeline views.
 *
 * Both the by-client accordion and the flat all-quotes table render the same
 * levers from the same component, so a quote cannot offer different actions
 * depending on which view the rep happens to be in. The status buttons are the
 * hygiene action and sit first; conversion is secondary and deliberately
 * quieter, because most passes should end in a decision rather than a tuned
 * percentage.
 *
 * "Still live" is not a status write. It stamps a review timestamp so a quote
 * the rep has genuinely checked stops re-flagging, without pretending a
 * decision was made. Tharstern has no field for it and should not gain one.
 *
 * There is deliberately no "Won". An estimate becomes won by being converted
 * into a job, which is where Tharstern sets ORDERED itself. Offering a rep a
 * manual Won button would invent a second way to reach the same status and let
 * the two disagree.
 */
import {
  formatCurrencyGbpCompact,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import type {
  HygieneFlag,
  PipelineQuote,
} from "@shared-core/data/my-pipeline-types";
import { Button } from "../../../primitives/button";
import { ProvenanceValue } from "../../../patterns/ProvenanceValue";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../primitives/tooltip";
import type { InterventionRequest } from "./InterventionDialog";

export type OnIntervene = (request: InterventionRequest) => void;

export function quoteFlagLabel(flag: HygieneFlag): string {
  switch (flag) {
    case "written_off":
      return "Carrying nothing";
    case "stale_status":
      return "No decision yet";
    case "promised_date_missed":
      return "Promised date missed";
  }
}

/**
 * Below this, the in-month figure is rounded to "≈£0".
 *
 * A quote on the very last inch of the curve produces arithmetically real but
 * operationally meaningless numbers — £57, £292. Printing them invites a rep to
 * read precision that isn't there, and makes the column scan as noise. The
 * question this figure answers is binary: is this quote holding up my month?
 * The exact pence stay in the tooltip for anyone reconciling.
 */
const NEGLIGIBLE_GBP = 500;

/**
 * Percentages round to whole numbers, except that a live-but-nearly-exhausted
 * quote must never print "0%" — that is the written-off reading, and these are
 * exactly the quotes a rep still has to decide.
 */
export function formatSharePct(share: number): string {
  if (share <= 0) return "0%";
  if (share < 0.005) return "<1%";
  return `${Math.round(share * 100)}%`;
}

/**
 * The two money figures, always shown together.
 *
 * In-month is the headline because it is what the month-landing chart counts,
 * and reconciliation to that chart is how the tool earns trust. Total remaining
 * sits under it because it is the number the rep holds in their head about the
 * quote, and hiding it would make the headline look wrong rather than precise.
 */
export function QuoteFigures({
  quote,
  monthLabel,
  onIntervene,
}: {
  quote: PipelineQuote;
  monthLabel: string;
  onIntervene: OnIntervene;
}) {
  const writtenOff = quote.conversion_share <= 0;
  const negligible = !writtenOff && quote.weighted_in_month < NEGLIGIBLE_GBP;
  const muted = writtenOff || negligible;
  return (
    <div className="w-40 shrink-0 text-right">
      <div className="flex items-baseline justify-end gap-1.5">
        <span
          className={`font-semibold tabular-nums ${muted ? "text-muted-foreground" : ""}`}
        >
          {writtenOff
            ? "£0"
            : negligible
              ? "≈£0"
              : formatCurrencyGbpCompact(quote.weighted_in_month)}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help text-xs text-muted-foreground underline decoration-dotted">
              {formatSharePct(quote.conversion_share)}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {writtenOff ? (
              <>
                {formatCurrencyGbpCompact(quote.quoted_value)} quoted,{" "}
                {quote.age_wd} working days old. Past the win curve&rsquo;s
                tail, so it carries nothing until you decide it.
              </>
            ) : (
              <>
                {formatCurrencyGbpCompact(quote.quoted_value)} quoted ×{" "}
                {formatSharePct(quote.conversion_share)} remaining ={" "}
                {formatCurrencyGbpCompact(quote.weighted_remaining_total)} still
                to come. Of that,{" "}
                {formatCurrencyGbpCompact(quote.weighted_in_month)} is expected
                to book inside this month.
              </>
            )}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
        <span className="tabular-nums">
          {formatCurrencyGbpCompact(quote.quoted_value)} · {quote.age_wd}wd
        </span>
        <span aria-hidden>·</span>
        <QuoteExpectedDate
          quote={quote}
          monthLabel={monthLabel}
          onIntervene={onIntervene}
        />
      </div>
    </div>
  );
}

export function QuoteLevers({
  quote,
  monthLabel,
  onIntervene,
}: {
  quote: PipelineQuote;
  monthLabel: string;
  onIntervene: OnIntervene;
}) {
  const subject = {
    quoteId: quote.id,
    subjectLabel: quote.reference,
    subjectDetail: quote.description,
    monthLabel,
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1">
      <Button
        size="sm"
        variant="outline"
        onClick={() =>
          onIntervene({ ...subject, lever: { kind: "status", target: "lost" } })
        }
      >
        Lost
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() =>
          onIntervene({
            ...subject,
            lever: { kind: "status", target: "still_live" },
          })
        }
      >
        Still live
      </Button>
      <ProvenanceValue
        value={formatSharePct(quote.conversion_share)}
        origin={quote.conversion_override ? "rep" : "derived"}
        attribution={quote.conversion_override}
        derivedExplanation={
          <>
            The win curve gives this quote{" "}
            {formatSharePct(quote.conversion_share)} remaining at {quote.age_wd}{" "}
            working days old.
          </>
        }
        revertLabel="Use the curve"
        className="px-2 py-1 text-sm"
        onClick={() =>
          onIntervene({
            ...subject,
            lever: { kind: "conversion" },
            currentShare: quote.conversion_share,
          })
        }
      />
    </div>
  );
}

/**
 * When the quote is expected to convert — derived from the bucket's own
 * quote-to-order median, or held at a date the rep was given.
 *
 * Sits with the figures rather than the buttons because it is a fact about the
 * quote that the rep reads far more often than they change.
 */
export function QuoteExpectedDate({
  quote,
  monthLabel,
  onIntervene,
}: {
  quote: PipelineQuote;
  monthLabel: string;
  onIntervene: OnIntervene;
}) {
  const { expected_conversion: expected } = quote;
  const passed = quote.flags.includes("promised_date_missed");
  return (
    <ProvenanceValue
      value={
        <span className={passed ? "text-amber-700 dark:text-amber-400" : ""}>
          {formatShortDateOrDash(expected.date)}
        </span>
      }
      origin={expected.origin}
      attribution={expected.attribution}
      derivedExplanation={
        <>
          {quote.bucket_name} usually converts {expected.derived_median_wd}{" "}
          working day{expected.derived_median_wd === 1 ? "" : "s"} after
          quoting, which puts this at{" "}
          {formatShortDateOrDash(expected.derived_date)}.
        </>
      }
      revertLabel="Use the worked-out date"
      className="text-xs"
      onClick={() =>
        onIntervene({
          quoteId: quote.id,
          subjectLabel: quote.reference,
          subjectDetail: quote.description,
          lever: { kind: "expected_date" },
          currentDate: expected.date,
          derivedDate: expected.derived_date,
          derivedMedianWd: expected.derived_median_wd,
          monthLabel,
        })
      }
    />
  );
}
